import React, { useEffect, useState } from 'react'
import Section from '../Section'
import SuccessStoriesCard from '../SuccessStoriesCard'
import { SuccessStoriesOverviewQuery, formatGraphQLResponse } from '/lib/reactutils/graphQL.js'
import Loading from '../Loading'

interface props {
    title?: string
    endpoint: string
    urlStart?: string
    currentSuccessStoryId?: string
    highlightedSuccessStoryId?: string
    pageSize: number
    highlightedSuccessStory?: {
        title: string
        taglineHeading: string
        url: string
        imgSrc: string,
        imgSrcSmall: string,
        alt: string
    }
    strings: {
        loadMore: string // Load more
    }
}

function SuccessStoriesOverview({ title, endpoint, urlStart = '', currentSuccessStoryId, highlightedSuccessStoryId, pageSize, highlightedSuccessStory, strings }: props) {
    const [stories, setStories] = useState([])
    const [endCursor, setEndCursor] = useState()
    const [hasNextPage, setHasNextPage] = useState(false)
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false)

    async function fetchStories(endCursor) {
        const response = await fetch(endpoint, {
            method: 'POST',
            body: JSON.stringify({ query: SuccessStoriesOverviewQuery(pageSize || 6, currentSuccessStoryId, highlightedSuccessStoryId, endCursor) })
        })
        const data = await response.json()
        const formattedData = formatGraphQLResponse(data, endpoint, urlStart)
        return formattedData
    }

    useEffect(() => {
        async function fetchFirstStories() {
            const firstStoriesData = await fetchStories(undefined)
            setStories(firstStoriesData.items)
            setEndCursor(firstStoriesData.endCursor)
            setHasNextPage(firstStoriesData.hasNextPage)
        }

        fetchFirstStories()
    }, [])

    async function handleLoadMore() {
        setIsLoadingMore(true)
        const moreStoriesData = await fetchStories(endCursor)
        setStories(prevState => [...prevState, ...moreStoriesData.items])
        setEndCursor(moreStoriesData.endCursor)
        setHasNextPage(moreStoriesData.hasNextPage)
        setIsLoadingMore(false)
    }

    return (
        <>
            <Section
                textColor='text-white'
                bgColor='bg-primary'
                className='top-section'
            >
                <div className='grid-container'>
                    <h1 className='title-lg title'>
                        {title}
                    </h1>
                </div>
            </Section>

            {highlightedSuccessStory ? <Section
                textColor='text-light'
                bgColor='bg-primary'
                className='featured-stories'
            >
                <div className='grid-container'>
                    <SuccessStoriesCard
                        featured={true}
                        href={{
                            url: highlightedSuccessStory.url,
                            isExternal: false
                        }}
                        img={highlightedSuccessStory.imgSrc && {
                            src: highlightedSuccessStory.imgSrc,
                            srcSmall: highlightedSuccessStory.imgSrcSmall,
                            alt: highlightedSuccessStory.alt,
                        }}
                        subtitle={highlightedSuccessStory.taglineHeading}
                        title={highlightedSuccessStory.title}
                    />
                </div>
            </Section> : <></>}

            <Section
                textColor='text-light'
                bgColor='bg-primary'
                className='all-stories'
            >
                <div className='grid-container'>

                    {stories?.length > 0 ? (
                        <div className='grid-row'>
                            {stories.map((item, i) => (
                                <div className='c-sm-6 c-lg-4' key={i}>
                                    <SuccessStoriesCard
                                        featured={false}
                                        href={{
                                            url: item.pageUrl,
                                        }}
                                        img={item.image?.imageUrl && {
                                            src: item.image.imageUrl,
                                            alt: item.img?.alt
                                        }}
                                        subtitle={item.tagline_heading}
                                        title={item.title}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ padding: '10rem 0' }}>
                            <Loading color="light" />
                        </div>
                    )}

                    {isLoadingMore ? (
                        <div className='load-more'>
                            <button onClick={handleLoadMore}>
                                Loading...
                            </button>
                        </div>
                    ) : (
                        hasNextPage ? (
                            <div className='load-more'>
                                <button onClick={handleLoadMore}>
                                    {strings.loadMore} <svg width='14' height='14' fill='currentColor' viewBox='0 0 14 14' xmlns='http://www.w3.org/2000/svg'><path d='M7.6998 0.800049V6.30005H13.1998V7.70005H7.6998V13.2H6.2998V7.70005H0.799805V6.30005H6.2998V0.800049H7.6998Z' /></svg>
                                </button>
                            </div>
                        ) : <></>
                    )}

                </div>
            </Section>
        </>
    )
}

export default SuccessStoriesOverview